import { useMemo, type ReactNode } from "react";
import { parseCommentMarkdown, type InlineToken } from "../lib/markdown";
import styles from "./Markdown.module.css";

/**
 * Renders one inline token to a React element. Content is plain text end to
 * end (the tokenizer never produces markup), so React's own text escaping is
 * the sanitiser — no dangerouslySetInnerHTML anywhere in the path (DEC-JC-005).
 */
function renderToken(token: InlineToken, key: number): ReactNode {
  switch (token.type) {
    case "text":
      return <span key={key}>{token.value}</span>;
    case "code":
      return (
        <code key={key} className={styles.code}>
          {token.value}
        </code>
      );
    case "bold":
      return <strong key={key}>{token.value}</strong>;
    case "italic":
      return <em key={key}>{token.value}</em>;
    case "link":
      return (
        <a key={key} href={token.href} target="_blank" rel="noopener noreferrer nofollow">
          {token.text}
        </a>
      );
    case "mention":
      return (
        <span key={key} className={styles.mention}>
          @{token.name || "user"}
        </span>
      );
  }
}

/**
 * Renders a comment's markdown subset to React elements: paragraphs (soft line
 * breaks preserved), bulleted lists, links, bold, italic, inline code, and
 * mention chips. Anything the tokenizer cannot read renders as literal text.
 *
 * @param {object} props
 * @param {string} props.source - Markdown source (a comment body).
 */
export function Markdown({ source }: { source: string }) {
  const blocks = useMemo(() => parseCommentMarkdown(source), [source]);
  return (
    <div className={styles.root}>
      {blocks.map((block, index) =>
        block.type === "paragraph" ? (
          <p key={index}>
            {block.lines.map((line, lineIndex) => (
              <span key={lineIndex}>
                {lineIndex > 0 && <br />}
                {line.map((token, tokenIndex) => renderToken(token, tokenIndex))}
              </span>
            ))}
          </p>
        ) : (
          <ul key={index}>
            {block.items.map((item, itemIndex) => (
              <li key={itemIndex}>{item.map((token, tokenIndex) => renderToken(token, tokenIndex))}</li>
            ))}
          </ul>
        ),
      )}
    </div>
  );
}
